import React, { useEffect } from 'react';
import Fade from '@material-ui/core/Fade';
import { FormattedMessage } from 'react-intl';
import { connect, ConnectedProps } from 'react-redux';
import { SuccessCheckmark } from '@tink/core/src/components/atoms/Icons/SuccessCheckmark';
import { AnimationZoomIn } from '@tink/core/src/components/atoms/AnimationZoomIn/AnimationZoomIn';
import { Box } from '@tink/core/src/components/atoms/Box/Box';
import { PrimaryContentColumn } from '../../../../../components/atoms/ContentBody/ContentBody';
import { ContentOverlay } from '../../../../../components/molecules/ContentOverlay/ContentOverlay';
import { Button } from '../../../../../components/atoms/Button/Button';
import { AppState } from '../../../../../stores/redux-store';
import { useAnalytics } from '../../../../Analytics/AnalyticsProvider';
import { ApplicationEvent } from '../../../../../models/analytics';
import { OverlayContainer } from '../../../LoadingOverlay/molecules/OverlayContainer/OverlayContainer';
import { messages } from './messages';

type SuccessOverlayProps = PropsFromRedux & {
  text?: JSX.Element;
  autoRedirectFailed?: boolean;
  redirectUri: string;
  subtext?: React.ReactNode;
};

export const SuccessOverlayPresenter: React.FC<SuccessOverlayProps> = ({
  text,
  autoRedirectFailed,
  redirectUri,
  clientName,
  subtext,
}) => {
  const { tinklinkAnalytics } = useAnalytics();

  useEffect(() => {
    if (autoRedirectFailed) {
      tinklinkAnalytics.sendApplicationEvent(ApplicationEvent.REDIRECT_FAILED);
    }
  }, [tinklinkAnalytics, autoRedirectFailed]);

  const overlayText = autoRedirectFailed ? (
    <FormattedMessage {...messages.goBackToOrigin} values={{ clientName }} />
  ) : (
    text
  );

  return (
    <ContentOverlay>
      <PrimaryContentColumn centerVertically>
        <OverlayContainer
          figure={<SuccessCheckmark fontSize="xlarge" color="primary" />}
          text={overlayText}
          delay={12}
        />
      </PrimaryContentColumn>
      <Box textAlign="center">
        <Fade in={autoRedirectFailed} timeout={200}>
          <Button
            href={redirectUri}
            color="primary"
            target="_blank"
            rel="noopener"
            variant="contained"
          >
            <FormattedMessage {...messages.failedRedirectButtonText} values={{ clientName }} />
          </Button>
        </Fade>
        {subtext && (
          <Box fullWidth px="xxsmall" py="xxsmall" mt="xxsmall">
            <AnimationZoomIn delay={12}>
              <Box mb="small">{subtext}</Box>
            </AnimationZoomIn>
          </Box>
        )}
      </Box>
    </ContentOverlay>
  );
};

const mapStateToProps = (state: AppState) => {
  return {
    clientName: state.oauth.description?.clientName,
  };
};

const connector = connect(mapStateToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export const SuccessOverlay = connector(SuccessOverlayPresenter);
